import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Activity, MessageCircle, Wrench, Loader2, CheckCircle2, Lock, Camera } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "@/pages/it/Aset";

const WA_NUMBER = "6282279614196";

const compressImage = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    URL.revokeObjectURL(url);
    const MAX_DIM = 1920;
    let { width, height } = img;
    if (Math.max(width, height) > MAX_DIM) {
      const scale = MAX_DIM / Math.max(width, height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    canvas.getContext("2d").drawImage(img, 0, 0, width, height);
    let quality = 0.85;
    const tryExport = () => {
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error("compress"));
        if (blob.size > 3 * 1024 * 1024 && quality > 0.3) {
          quality -= 0.15;
          tryExport();
        } else {
          resolve(new File([blob], "bukti.jpg", { type: "image/jpeg" }));
        }
      }, "image/jpeg", quality);
    };
    tryExport();
  };
  img.onerror = () => reject(new Error("load"));
  img.src = url;
});

export default function ScanAsset() {
  const { assetId } = useParams();
  const [asset, setAsset] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [verified, setVerified] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({ detail_pekerjaan: "", status: "Baik", rekomendasi_pembelian: "", teknisi: "" });
  const [submitting, setSubmitting] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [compressing, setCompressing] = useState(false);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCompressing(true);
    try {
      const compressed = await compressImage(file);
      setPhoto(compressed);
      setPhotoPreview(URL.createObjectURL(compressed));
      toast.success(`Foto dikompres otomatis: ${(compressed.size / 1024 / 1024).toFixed(2)} MB`);
    } catch {
      toast.error("Gagal memproses foto");
    } finally {
      setCompressing(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/public/assets/${assetId}`)
      .then((r) => setAsset(r.data))
      .catch(() => setNotFound(true));
  }, [assetId]);

  const waText = asset
    ? encodeURIComponent(`PENGADUAN KENDALA ASET IT\nKode: ${asset.kode}\nNama: ${asset.nama}\nRuangan: ${asset.ruangan}\n\nKendala yang dialami: `)
    : "";

  const verify = async () => {
    try {
      await axios.post(`${API}/public/maintenance/verify`, { access_code: accessCode });
      setVerified(true);
    } catch {
      toast.error("Kode akses salah");
    }
  };

  const submit = async () => {
    if (!form.detail_pekerjaan) {
      toast.error("Detail pekerjaan wajib diisi");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/maintenance`, {
        access_code: accessCode,
        asset_id: assetId,
        detail_pekerjaan: form.detail_pekerjaan,
        status: form.status,
        rekomendasi_pembelian: form.status === "Rusak" ? null : form.rekomendasi_pembelian || null,
        teknisi: form.teknisi || null,
      });
      if (photo) {
        const fd = new FormData();
        fd.append("access_code", accessCode);
        fd.append("maintenance_id", data.id);
        fd.append("file", photo);
        await axios.post(`${API}/public/maintenance/foto`, fd);
      }
      setDone(data);
      toast.success("Laporan maintenance tersimpan");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSubmitting(false);
    }
  };

  const closeDialog = (open) => {
    setDialogOpen(open);
    if (!open) {
      setAccessCode("");
      setVerified(false);
      setDone(null);
      setForm({ detail_pekerjaan: "", status: "Baik", rekomendasi_pembelian: "", teknisi: "" });
      setPhoto(null);
      setPhotoPreview(null);
    }
  };

  if (notFound)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="scan-not-found">
        <div className="text-center">
          <p className="font-heading font-bold text-2xl text-primary">SIPAT</p>
          <p className="mt-4 text-muted-foreground">Aset tidak ditemukan. Periksa kembali QR code.</p>
        </div>
      </div>
    );

  if (!asset)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="scan-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );

  return (
    <div className="min-h-screen bg-background" data-testid="scan-page">
      <div className="bg-primary px-6 py-5 flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-white/15 flex items-center justify-center">
          <Activity className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="font-heading font-bold text-xl text-white tracking-tight" data-testid="scan-brand">SIPAT</p>
          <p className="text-emerald-100/80 text-xs">Sistem Informasi Pemeliharaan Aset Teknologi Informasi</p>
        </div>
      </div>

      <div className="max-w-md mx-auto p-6 space-y-6">
        <div className="bg-card border border-border rounded-lg shadow-sm p-6" data-testid="scan-asset-detail">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">{asset.kode}</p>
              <h1 className="font-heading font-semibold text-2xl mt-1" data-testid="scan-asset-name">{asset.nama}</h1>
            </div>
            <StatusBadge status={asset.status} />
          </div>
          <dl className="mt-5 space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-muted-foreground">Kategori</dt><dd className="font-medium">{asset.kategori || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Ruangan</dt><dd className="font-medium" data-testid="scan-asset-room">{asset.ruangan || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-muted-foreground">Merk</dt><dd className="font-medium">{asset.merk || "—"}</dd></div>
          </dl>
          {asset.spesifikasi && <p className="mt-4 text-xs text-muted-foreground border-t border-border pt-3">{asset.spesifikasi}</p>}
        </div>

        <div className="space-y-3">
          <a href={`https://wa.me/${WA_NUMBER}?text=${waText}`} target="_blank" rel="noopener noreferrer" className="block">
            <Button className="w-full h-12 text-base bg-[#25D366] hover:bg-[#1eb857] text-white" data-testid="pengaduan-whatsapp-button">
              <MessageCircle className="w-5 h-5 mr-2" /> Pengaduan Kendala (WhatsApp)
            </Button>
          </a>
          <Button variant="outline" className="w-full h-12 text-base border-primary text-primary hover:bg-accent" onClick={() => setDialogOpen(true)} data-testid="maintenance-it-button">
            <Wrench className="w-5 h-5 mr-2" /> Maintenance IT
          </Button>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={closeDialog}>
        <DialogContent data-testid="maintenance-dialog">
          <DialogHeader>
            <DialogTitle>Maintenance IT — {asset.kode}</DialogTitle>
            <DialogDescription>Form maintenance internal dengan kode akses.</DialogDescription>
          </DialogHeader>

          {done ? (
            <div className="text-center py-6 space-y-3" data-testid="maintenance-success">
              <CheckCircle2 className="w-12 h-12 text-primary mx-auto" />
              <p className="font-semibold">Laporan maintenance tersimpan</p>
              <p className="text-sm text-muted-foreground">Tanggal: {done.tanggal}</p>
              <Button onClick={() => closeDialog(false)} data-testid="maintenance-close-button">Tutup</Button>
            </div>
          ) : !verified ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Lock className="w-4 h-4" /> Form ini dilindungi kode akses internal IT.
              </div>
              <div className="space-y-2">
                <Label>Kode Akses</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && verify()}
                  placeholder="Masukkan kode akses"
                  data-testid="access-code-input"
                />
              </div>
              <Button className="w-full" onClick={verify} data-testid="verify-code-button">Verifikasi Kode</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Detail Pekerjaan</Label>
                <Textarea
                  value={form.detail_pekerjaan}
                  onChange={(e) => setForm({ ...form, detail_pekerjaan: e.target.value })}
                  placeholder="Uraikan pekerjaan maintenance yang dilakukan"
                  data-testid="maintenance-form-detail"
                />
              </div>
              <div className="space-y-2">
                <Label>Status Hasil</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="maintenance-form-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Baik">Baik</SelectItem>
                    <SelectItem value="Rusak">Rusak</SelectItem>
                    <SelectItem value="Perbaikan">Perbaikan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Nama Teknisi (opsional)</Label>
                <Input value={form.teknisi} onChange={(e) => setForm({ ...form, teknisi: e.target.value })} data-testid="maintenance-form-teknisi" />
              </div>
              {form.status !== "Rusak" && (
                <>
                  <div className="space-y-2">
                    <Label>Rekomendasi Pembelian (opsional)</Label>
                    <Textarea
                      value={form.rekomendasi_pembelian}
                      onChange={(e) => setForm({ ...form, rekomendasi_pembelian: e.target.value })}
                      placeholder="Contoh: Ganti PSU 500W"
                      data-testid="maintenance-form-rekomendasi"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground bg-accent rounded-md px-3 py-2" data-testid="maintenance-next-schedule-info">
                    Tanggal hari ini tercatat otomatis.
                  </p>
                </>
              )}
              <div className="space-y-2">
                <Label>Bukti Foto (opsional)</Label>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  id="foto-bukti-input"
                  className="hidden"
                  onChange={handlePhoto}
                  data-testid="maintenance-form-foto"
                />
                <label
                  htmlFor="foto-bukti-input"
                  className="flex items-center justify-center gap-2 border border-dashed border-border rounded-md px-3 py-3 text-sm cursor-pointer hover:bg-accent transition-colors"
                  data-testid="maintenance-form-foto-label"
                >
                  <Camera className="w-4 h-4 text-primary" />
                  {compressing ? "Mengompres foto..." : photo ? "Ganti Foto" : "Ambil Foto / Pilih dari Galeri"}
                </label>
                {photoPreview && (
                  <div className="flex items-center gap-3">
                    <img src={photoPreview} alt="Preview bukti" className="w-16 h-16 object-cover rounded-md border border-border" data-testid="foto-preview" />
                    <p className="text-xs text-muted-foreground">{(photo.size / 1024 / 1024).toFixed(2)} MB (maks 3 MB)</p>
                  </div>
                )}
              </div>
              <Button className="w-full" onClick={submit} disabled={submitting || compressing} data-testid="maintenance-form-submit">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Simpan Laporan Maintenance
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
